// src/pages/Welcome.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Welcome.css";

const LOGO_DARK =
  "https://yptraining.romancontrols.com/images/romanworks-trans-white.png";
const LOGO_LIGHT =
  "https://yptraining.romancontrols.com/images/romanworks-trans-black.png";

const FEATURES = [
  {
    icon: "🔒",
    title: "Real-Time Lock Control",
    description:
      "Monitor and control smart locks instantly across all your facilities. Lock, unlock, and check device state from anywhere with sub-second response times.",
  },
  {
    icon: "📱",
    title: "Mobile Credential Provisioning",
    description:
      "Issue, suspend, and revoke mobile credentials directly to end users' smartphones. Full lifecycle management powered by Allegion CLM.",
  },
  {
    icon: "⚡",
    title: "Live Event Streaming",
    description:
      "Watch access events unfold in real time via the DevStream panel. Filter by device, credential, or event type to stay on top of what matters.",
  },
  {
    icon: "🏢",
    title: "Multi-Tenant Organization Management",
    description:
      "Manage multiple organizations and sites from a single dashboard. Role-based access ensures every user sees only what they should.",
  },
  {
    icon: "🔗",
    title: "WebSocket-Powered Updates",
    description:
      "No refresh needed. The platform pushes credential and device state changes to your browser the moment they happen via persistent WebSocket connections.",
  },
  {
    icon: "🛡️",
    title: "Enterprise-Grade Security",
    description:
      "OAuth 2.0 PKCE authentication, JWT-validated API calls, and end-to-end encryption keep your access control data safe at every layer.",
  },
];

const Welcome = () => {
  const [logoSrc, setLogoSrc] = useState("");

  useEffect(() => {
    document.title = "Welcome — Romanworks Real-Time Device Management";

    const savedTheme = localStorage.getItem("theme") || "dark-mode";
    document.body.className = savedTheme === "light-mode" ? "light-mode" : "";

    setLogoSrc(savedTheme === "light-mode" ? LOGO_LIGHT : LOGO_DARK);
  }, []);

  return (
    <div className="welcome-page">
      {/* Nav */}
      <nav className="welcome-nav">
        <div className="welcome-nav-inner">
          {logoSrc && (
            <img src={logoSrc} alt="Romanworks" className="welcome-nav-logo" />
          )}
          <div className="welcome-nav-links">
            <Link to="/support" className="welcome-nav-link">
              Support
            </Link>
            <Link to="/login" className="welcome-nav-cta">
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="welcome-hero">
        <div className="welcome-hero-inner">
          <div className="welcome-hero-badge">
            Powered by the{" "}
            <a
              href="https://developers.yonomi.cloud/getting-started/introduction"
              target="_blank"
              rel="noopener noreferrer"
              className="welcome-badge-link"
            >
              Allegion Yonomi Platform
            </a>
          </div>
          <h1 className="welcome-hero-heading">
            Real-Time Smart Lock Management,{" "}
            <span className="welcome-hero-accent">Reimagined</span>
          </h1>
          <p className="welcome-hero-sub">
            Romanworks gives facilities teams, integrators, and security
            administrators a single pane of glass for every smart lock, mobile
            credential, and access event across their entire portfolio.
          </p>
          <div className="welcome-hero-actions">
            <Link to="/login" className="welcome-btn-primary">
              Get Started
            </Link>
            <Link to="/support" className="welcome-btn-secondary">
              Contact Support
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="welcome-features">
        <div className="welcome-section-inner">
          <h2 className="welcome-section-heading">
            Everything you need to manage access at scale
          </h2>
          <p className="welcome-section-sub">
            From a single door to an enterprise campus, Romanworks adapts to
            your deployment.
          </p>
          <div className="welcome-feature-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="welcome-feature-card">
                <div className="welcome-feature-icon">{f.icon}</div>
                <h3 className="welcome-feature-title">{f.title}</h3>
                <p className="welcome-feature-desc">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="welcome-cta-band">
        <div className="welcome-section-inner welcome-cta-inner">
          <div>
            <h2 className="welcome-cta-heading">Ready to take control?</h2>
            <p className="welcome-cta-sub">
              Sign in to your Romanworks account or reach out to request access.
            </p>
          </div>
          <div className="welcome-cta-actions">
            <Link to="/login" className="welcome-btn-primary">
              Sign In
            </Link>
            <a
              href="https://yptraining.romancontrols.com/romanworks-userguide"
              target="_blank"
              rel="noopener noreferrer"
              className="welcome-btn-secondary"
            >
              Request Access
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="welcome-footer">
        <p>
          &copy; {new Date().getFullYear()} Romanworks &mdash; Powered by
          <a
            href="https://developers.yonomi.cloud/getting-started/getting-started"
            target="_blank"
            rel="noopener noreferrer"
          >
            Allegion Yonomi Platform
          </a>{" "}
          &nbsp;|&nbsp;
          <a
            href="https://realtime.romancontrols.com/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          &nbsp;|&nbsp;
          <Link to="/support">Support</Link>
        </p>
      </footer>
    </div>
  );
};

export default Welcome;
